/**
 * The brief being written, held across the three screens that share it.
 *
 * Write collects a progression and a roster, Score reads the verdict on what came back, and
 * Export hands over the file. They are one piece of work, so the result lives above all three
 * rather than being re-orchestrated each time a reader steps back and forward.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { WritePayload } from "@app/shared-types";
import { api } from "../api/client";

export type Regime = "forte" | "piano";

interface Brief {
  chords: string[];
  roster: string[];
  regime: Regime;
  result: WritePayload | null;
  running: boolean;
  error: string | null;
  setChords: (chords: string[]) => void;
  setRoster: (roster: string[]) => void;
  setRegime: (regime: Regime) => void;
  orchestrate: () => Promise<WritePayload | null>;
}

const BriefContext = createContext<Brief | null>(null);

/** The board's own opening brief: the progression and the brass the Write screen is drawn with. */
const OPENING = {
  chords: ["C3 E G", "G2 B D F"],
  roster: ["horn", "horn", "trumpet", "trombone", "tuba"],
};

export function BriefProvider({ children }: { children: ReactNode }): JSX.Element {
  const [chords, setChords] = useState<string[]>(OPENING.chords);
  const [roster, setRoster] = useState<string[]>(OPENING.roster);
  const [regime, setRegime] = useState<Regime>("forte");
  const [result, setResult] = useState<WritePayload | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orchestrate = useCallback(async (): Promise<WritePayload | null> => {
    setRunning(true);
    setError(null);
    try {
      const payload = await api.write({ chords, roster, regime });
      setResult(payload);
      return payload;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setResult(null);
      return null;
    } finally {
      setRunning(false);
    }
  }, [chords, roster, regime]);

  const value = useMemo<Brief>(
    () => ({
      chords,
      roster,
      regime,
      result,
      running,
      error,
      setChords,
      setRoster,
      setRegime,
      orchestrate,
    }),
    [chords, roster, regime, result, running, error, orchestrate],
  );

  return <BriefContext.Provider value={value}>{children}</BriefContext.Provider>;
}

export function useBrief(): Brief {
  const found = useContext(BriefContext);
  if (found === null) throw new Error("useBrief must be used inside a BriefProvider");
  return found;
}
